import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Loader2,
  RefreshCw,
  Zap,
  Play,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

/**
 * Auto-Pay Readiness panel — iter306ae.
 *
 * Mounted at the top of the Saved PMs tab to give admins a one-glance
 * view of "how many enrolled parents will auto-charge tonight" PLUS
 * the recovery actions for when the cron didn't fire:
 *   • Run monthly billing now (manual trigger for the cron)
 *   • Bulk backfill auto_pay_acknowledged defaults (dry-run + apply)
 *   • Force-charge single parent (deep-linked from each row via
 *     onChargeParent callback — actual rows live in the Saved PMs
 *     table below, so we don't duplicate the table here).
 *
 * Why this lives here instead of a separate Auto-Pay tab: the Saved
 * PMs tab is already the "payment-method truth" view. Putting the
 * readiness diagnostic next to it means admins don't have to bounce
 * between two pages to understand a parent's billing state.
 */
const REASON_LABEL = {
  ok: 'Ready',
  ok_no_pm_check: 'Ready (PM not verified)',
  no_ack: 'Needs ack',
  no_stripe_customer: 'No Stripe customer',
  no_saved_pm: 'No saved PM',
  no_ack_no_customer: 'No ack + no customer',
  stripe_lookup_failed: 'Stripe lookup failed',
};

const REASON_TONE = {
  ok: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  ok_no_pm_check: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  no_ack: 'bg-amber-100 text-amber-800 border-amber-300',
  no_stripe_customer: 'bg-amber-100 text-amber-800 border-amber-300',
  no_saved_pm: 'bg-rose-100 text-rose-800 border-rose-300',
  no_ack_no_customer: 'bg-amber-100 text-amber-800 border-amber-300',
  stripe_lookup_failed: 'bg-rose-100 text-rose-800 border-rose-300',
};

export default function AutoPayReadinessPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [runningCron, setRunningCron] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const fetchData = useCallback(async (verifyPMs = false) => {
    setLoading(true);
    try {
      const res = await api.get(
        `/admin/billing/autopay-readiness?include_pm_lookup=${verifyPMs ? 'true' : 'false'}`
      );
      setData(res.data);
      if (verifyPMs) toast.success('Stripe PM verification complete');
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(`Auto-Pay readiness lookup failed: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(false); }, [fetchData]);

  const runMonthlyBilling = async () => {
    if (!window.confirm(
      'Manually trigger the monthly billing cron NOW?\n\n' +
      'This walks every active enrollment and creates a monthly_invoice ' +
      'for the current billing month (if not already created). Safe to ' +
      're-run — the cron has a dedup guard.',
    )) return;
    setRunningCron(true);
    try {
      const res = await api.post('/admin/billing/run-monthly');
      const { invoices_created, auto_charged, errors } = res.data?.details || {};
      toast.success(
        `Cron complete: ${invoices_created ?? '?'} invoices created · ` +
        `${auto_charged ?? '?'} auto-charged · ${errors ?? '?'} errors`,
        { duration: 9000 },
      );
      fetchData();
    } catch (e) {
      toast.error(`Cron failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRunningCron(false);
    }
  };

  const runBackfill = async (dryRun) => {
    setBackfilling(true);
    try {
      const res = await api.post('/admin/billing/backfill-autopay-defaults', {
        dry_run: dryRun,
        force: false,
        method: 'both',
      });
      const eligible = res.data?.eligible_parents ?? 0;
      const wouldUpdate = res.data?.would_update ?? 0;
      const alreadySet = res.data?.skipped_already_set ?? 0;
      const missing = res.data?.missing_users ?? 0;
      // iter306ae — clearer phrasing per user feedback. The old text
      // "71 parents would be flipped (of 157 eligible)" hid the fact
      // that 86 were already configured.
      const msg = dryRun
        ? `Dry-run: ${wouldUpdate} of ${eligible} enrolled parents need auto-pay defaults set. ` +
          `${alreadySet} already configured. ${missing} have no user record.`
        : `Backfilled ${wouldUpdate} parents to auto_pay_acknowledged=true.`;
      toast.success(msg, { duration: 9000 });
      if (!dryRun) fetchData();
    } catch (e) {
      toast.error(`Backfill failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBackfilling(false);
    }
  };

  const summary = data?.summary || {};
  const totalParents = data?.total_parents || 0;
  // iter307ai — Count both the strict "ok" (verified PM > 0) AND the
  // fast-path "ok_no_pm_check" (DB-side gates passed, PM lookup skipped)
  // as Ready. Without this, the FE always rendered 0 because we call
  // the endpoint with include_pm_lookup=false by default.
  const readyCount = (summary.ok || 0) + (summary.ok_no_pm_check || 0);
  const notReadyCount = totalParents - readyCount;

  return (
    <Card className="border-indigo-200 bg-indigo-50/30" data-testid="autopay-readiness-panel">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">Auto-Pay Readiness</CardTitle>
            <CardDescription>
              How many enrolled parents will auto-charge on the next billing cycle, and the
              cron recovery tools when an invoice run is missed.
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => fetchData(false)}
              disabled={loading}
              data-testid="autopay-refresh-btn"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => fetchData(true)}
              disabled={loading}
              data-testid="autopay-verify-stripe-btn"
              title="Slow path: also call Stripe to confirm each parent actually has a saved payment method on file."
            >
              {loading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
              Verify PMs with Stripe
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => runBackfill(true)}
              disabled={backfilling}
              data-testid="autopay-backfill-dryrun-btn"
            >
              {backfilling ? 'Running…' : 'Dry-run backfill'}
            </Button>
            <Button
              size="sm"
              onClick={() => runBackfill(false)}
              disabled={backfilling}
              data-testid="autopay-backfill-apply-btn"
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Zap className="mr-1 h-3.5 w-3.5" />
              Bulk backfill
            </Button>
            <Button
              size="sm"
              onClick={runMonthlyBilling}
              disabled={runningCron}
              data-testid="autopay-run-cron-btn"
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {runningCron ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Play className="mr-1 h-3.5 w-3.5" />}
              Run monthly billing now
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <KpiTile
            label="Enrolled parents"
            value={totalParents}
            tone="slate"
            testid="autopay-kpi-total"
          />
          <KpiTile
            label="Ready to auto-charge"
            value={readyCount}
            tone="emerald"
            icon={CheckCircle2}
            testid="autopay-kpi-ready"
          />
          <KpiTile
            label="Not ready"
            value={notReadyCount}
            tone={notReadyCount > 0 ? 'rose' : 'slate'}
            icon={AlertTriangle}
            testid="autopay-kpi-not-ready"
          />
          <KpiTile
            label="Billing month"
            value={data?.billing_month || '—'}
            tone="slate"
            testid="autopay-kpi-month"
          />
        </div>

        {Object.keys(summary).length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            <span className="text-xs font-medium text-slate-500">Reason breakdown:</span>
            {Object.entries(summary).map(([code, count]) => (
              <Badge
                key={code}
                variant="outline"
                className={REASON_TONE[code] || 'bg-slate-100 text-slate-800'}
                data-testid={`autopay-reason-${code}`}
              >
                {REASON_LABEL[code] || code}: {count}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function KpiTile({ label, value, tone = 'slate', icon: Icon, testid }) {
  const tones = {
    slate: 'border-slate-200 bg-white',
    emerald: 'border-emerald-200 bg-emerald-50',
    rose: 'border-rose-200 bg-rose-50',
  };
  return (
    <div
      className={`rounded-md border px-3 py-2 ${tones[tone] || tones.slate}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-xs uppercase text-slate-500">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {label}
      </div>
      <div className="mt-0.5 text-xl font-semibold text-slate-900">{value}</div>
    </div>
  );
}
